import React, { useContext, useMemo } from 'react'

import { connect } from 'react-redux'
import { getTranslate } from 'react-localize-redux'
import { isArray, isFunction, isString } from 'lodash'
import { Menu, Select } from 'antd'
import { faHospital } from '@fortawesome/pro-regular-svg-icons'
import { faBusinessTime, faCalendar, faCalendarAlt, faChartPie, faSchool, faSort, faPassport } from '@fortawesome/pro-solid-svg-icons'

import IconLabel, { FontSizes } from '../../../../Components/icons/IconLabel'
import { isEncoder, isHospitalAdmin } from '../../../../utils/roles'
import DateTrimmer from '../../../../Components/shared/DateTrimmer'
import InternshipStatesOrderBy from '../../../../Components/shared/InternshipStatesOrderBy'
import { Contexts, OrderBy, OrderDirections } from '../../../../utils/constants'
import { DEFAULT_INTERNSHIP_FILTERS, GlobalFiltersContext } from '../../../../Providers/GlobalFiltersProvider'
import InstitutionsSelector from '../../../../Components/shared/Selectors/Institution/InstitutionsSelector'
import PeriodSelector from '../../../../Components/shared/Selectors/PeriodSelector'
import SchoolsSelector from '../../../../Components/shared/Selectors/School/SchoolsSelector'
import SectionsSelector from '../../../../Components/shared/Selectors/SectionsSelector'
import StatesSelector from '../../../../Components/shared/Selectors/StatesSelector'
import { OwnershipFilters } from '../../../../Components/shared/InternshipsManager/utils/constants'
import {
  CONTEXT_SHIFTS_INSTITUTION_KEY
} from '../../../../Components/shared/ShiftsManager/ShiftsManagerHeader/ActionsFieldsAndButtons'

const mapStateToProps = state => ({ t: getTranslate(state.locale) })

export const getActiveFiltersCount = (filters) => {
  const filtersState = filters ?? {}
  let count = 0

  if (filtersState.areStartedExcluded) {
    count += 1
  }

  if (isString(filtersState.institutions)) {
    count += 1
  }

  if (isString(filtersState.schools)) {
    count += 1
  }

  if (Array.isArray(filtersState.sections) && filtersState.sections.length > 0) {
    count += 1
  }

  if (Array.isArray(filtersState.states) && filtersState.states.length > 0) {
    count += 1
  }

  if (Array.isArray(filtersState.status) && filtersState.status.length > 0) {
    count += 1
  }

  if (typeof filtersState.weeksShownCount === 'number' &&
      filtersState.weeksShownCount !== DEFAULT_INTERNSHIP_FILTERS.weeksShownCount) {
    count += 1
  }

  if (filtersState.ownership && filtersState.ownership !== DEFAULT_INTERNSHIP_FILTERS.ownership) {
    count += 1
  }

  return count
}

const InternshipFilters = ({
  precision, precisionOptions, t, user, useOrder = true,
  onPrecisionChange, onFilterChange, context
}) => {
  const { internshipFilters, setInternshipFilters } = useContext(GlobalFiltersContext)
  const { areStartedExcluded, institutions, order, ownership, schools, sections, states, weeksShownCount } = internshipFilters

  const isEncoderUser = useMemo(() => isEncoder(user), [user])

  const Option = Select.Option

  const handleFilterChange = (value, key) => {
    onFilterChange()
    setInternshipFilters({ ...internshipFilters, [key]: value })
  }

  return (
    <Menu inlineIndent={0}>
      <div className='h-spacing' />
      <Menu.ItemGroup title={<IconLabel fontSize={FontSizes.SMALL} padding={0} icon={faBusinessTime} text={t('common.internship')} />}>
        <Menu.Item key='state' disabled style={{ cursor: 'default' }}>
          <StatesSelector
            context={context}
            padding={0}
            onCheck={s => handleFilterChange(s, 'states')}
            values={states}
          />
        </Menu.Item>
      </Menu.ItemGroup>

      {isEncoderUser && (<div className='h-spacing' />)}
      {isEncoderUser && (
        <Menu.ItemGroup title={<IconLabel fontSize={FontSizes.SMALL} padding={0} icon={faHospital} text={t('common.institutions')} />}>
          <Menu.Item key='institutions' disabled style={{ cursor: 'default' }}>
            <InstitutionsSelector
              key='institutionsSelector'
              values={institutions}
              onChange={i => handleFilterChange(i, 'institutions')}
              style={{ width: '210px' }}
            />
          </Menu.Item>
        </Menu.ItemGroup>
      )}

      {isEncoderUser && (<div className='h-spacing' />)}
      {isEncoderUser && (
        <Menu.ItemGroup title={<IconLabel fontSize={FontSizes.SMALL} padding={0} icon={faSchool} text={t('common.sections')} />}>
          <Menu.Item key='sections' disabled style={{ cursor: 'default' }}>
            <SectionsSelector
              key='sectionsSelector'
              values={sections}
              onChange={s => handleFilterChange(s, 'sections')}
              style={{ width: '210px' }}
            />
          </Menu.Item>
        </Menu.ItemGroup>
      )}

      {isHospitalAdmin(user) && (<div className='h-spacing' />)}
      {isHospitalAdmin(user) && (
        <Menu.ItemGroup title={<IconLabel fontSize={FontSizes.SMALL} padding={0} icon={faSchool} text={t('common.schools')} />}>
          <Menu.Item key='schools' disabled style={{ cursor: 'default' }}>
            <SchoolsSelector
              key='schoolsSelector'
              values={schools}
              onChange={s => handleFilterChange(s, 'schools')}
              style={{ width: '210px' }}
            />
          </Menu.Item>
        </Menu.ItemGroup>
      )}

      <Menu.ItemGroup title={<IconLabel fontSize={FontSizes.SMALL} padding={0} icon={faPassport} text={t('common.ownership')} />}>
        <Menu.Item key='config' disabled style={{ cursor: 'default', paddingBottom: 0 }}>
          <Select
            style={{ width: '210px' }}
            onSelect={o => handleFilterChange(o, 'ownership')}
            value={ownership}
          >
            {Object.keys(OwnershipFilters).map((o, index) => <Option key={o} value={o}> {t('common.' + o.toLowerCase())}  </Option>)}
          </Select>
        </Menu.Item>
      </Menu.ItemGroup>

      <Menu.ItemGroup title={<IconLabel fontSize={FontSizes.SMALL} padding={0} icon={faCalendar} text={t('internships_header.filters.date_trimmer')} />}>
        <Menu.Item key='filter-on-start-date' disabled style={{ cursor: 'default' }}>
          <DateTrimmer
            toggled={areStartedExcluded}
            onToggle={bool => handleFilterChange(bool, 'areStartedExcluded')}
          />
        </Menu.Item>
      </Menu.ItemGroup>

      {useOrder && (
        <Menu.ItemGroup title={<IconLabel fontSize={FontSizes.SMALL} padding={0} icon={faSort} text={t('common.order_by')} />}>
          <Menu.Item key='order' disabled style={{ cursor: 'default' }}>
            <InternshipStatesOrderBy
              value={order?.by ?? OrderBy.CREATED_AT}
              onChange={ob => setInternshipFilters({ ...internshipFilters, order: { ...internshipFilters.order, by: ob } })}
              direction={order?.direction ?? OrderDirections.DESC}
              onDirectionChange={sd => setInternshipFilters({ ...internshipFilters, order: { ...internshipFilters.order, direction: sd } })}
            />
          </Menu.Item>
        </Menu.ItemGroup>
      )}

      {user.context.includes(Contexts.INSTITUTION) && isArray(precisionOptions) && isFunction(onPrecisionChange) && (
        <Menu.ItemGroup title={<IconLabel fontSize={FontSizes.SMALL} padding={0} icon={faChartPie} text={t('common.quotas')} />}>
          <Menu.Item key='precision' disabled style={{ cursor: 'default' }}>
            <Select value={precision} onChange={onPrecisionChange} style={{ width: '210px' }}>
              {precisionOptions.map((o, index) => {
                return (
                  <Select.Option key={'opt_' + index} value={o.value}>
                    {o.label}
                  </Select.Option>
                )
              })}
            </Select>
          </Menu.Item>
        </Menu.ItemGroup>
      )}

      {context !== CONTEXT_SHIFTS_INSTITUTION_KEY && (
        <Menu.ItemGroup title={<IconLabel fontSize={FontSizes.SMALL} padding={0} icon={faCalendarAlt} text={t('common.period')} />}>
          <Menu.Item key='config' disabled style={{ cursor: 'default', paddingBottom: 0 }}>
            <PeriodSelector
              currentWeeksQuantity={weeksShownCount}
              onWeeksQuantityChange={w => handleFilterChange(w, 'weeksShownCount')}
            />
          </Menu.Item>
        </Menu.ItemGroup>
      )}

    </Menu>
  )
}

export default connect(mapStateToProps)(InternshipFilters)
